import { useGlobalProps } from '@hooks/useGlobalProps';
import { IS_TRANS_CODE_ENABLED } from '@constants/config';
import { useNavigate } from 'react-router-dom';
import { SWITCH_ROUTES } from '@constants/routes';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for trans code functionality
 * Returns state and handlers for trans code verification in withdrawal/transfer pages
 *
 * tc_mode values:
 * - 0: Not set (no trans code required)
 * - 1: Set (trans code required for verification)
 * - 2: Reset needed (redirect to profile)
 * - 6: Locked (show locked message)
 * - 9: Ignore (no trans code required)
 */
export default function useTransCode() {
    const { user } = useGlobalProps();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const tTransCode = t('transCode', { returnObjects: true });
    const tCommon = t('common', { returnObjects: true });

    const tcMode = user?.tc_mode;

    // Determine which state we're in
    const showTransCodeInput = IS_TRANS_CODE_ENABLED && tcMode === 1;
    const needsTransCodeSetup = IS_TRANS_CODE_ENABLED && tcMode === 2;
    const isTransCodeLocked = IS_TRANS_CODE_ENABLED && tcMode === 6;
    const isTransCodeEnabled = IS_TRANS_CODE_ENABLED && tcMode !== 0 && tcMode !== 9 && tcMode !== undefined;

    /**
     * Validates trans code before submission
     * @param {string} transCode - The trans code entered by user
     * @param {function} setAlert - Function to show alert modal
     * @returns {boolean} - True if validation passes, false otherwise
     */
    const validateTransCode = (transCode, setAlert) => {
        // If trans code not enabled or mode is 0/9, skip validation
        if (!isTransCodeEnabled || tcMode === 0 || tcMode === 9) {
            return true;
        }

        // tc_mode=2: Need to set trans code first
        if (needsTransCodeSetup) {
            setAlert({
                show: true,
                title: tCommon['common@failed'],
                message: tTransCode['transCode@pleaseSet'],
            });
            setTimeout(() => navigate(SWITCH_ROUTES.PROFILE), 1500);
            return false;
        }

        // tc_mode=6: Locked
        if (isTransCodeLocked) {
            setAlert({
                show: true,
                title: tCommon['common@failed'],
                message: tTransCode['transCode@locked'],
            });
            return false;
        }

        // tc_mode=1: Need trans code
        if (showTransCodeInput && !transCode) {
            setAlert({
                show: true,
                title: tCommon['common@failed'],
                message: tTransCode['transCode@required'],
            });
            return false;
        }

        return true;
    };

    return {
        tcMode,
        showTransCodeInput,
        needsTransCodeSetup,
        isTransCodeLocked,
        isTransCodeEnabled,
        validateTransCode,
        tTransCode,
    };
}
